// Type declarations for browser APIs
declare global {
  namespace NodeJS {
    interface Global {
      IntersectionObserver: any;
      ResizeObserver: any;
      performance: any;
    }
  }
}

/**
 * Mock IntersectionObserver for components that use ng-in-viewport or similar directives.
 *
 * This should be called in test setup before creating components that use IntersectionObserver.
 *
 * @example
 * ```typescript
 * import { mockIntersectionObserver } from '@frontend/shared';
 *
 * describe('YourComponent', () => {
 *   beforeEach(() => {
 *     mockIntersectionObserver();
 *   });
 * });
 * ```
 */
export function mockIntersectionObserver(): void {
  (globalThis as any).IntersectionObserver = function () {
    return {
      observe: () => {},
      unobserve: () => {},
      disconnect: () => {},
    };
  };
}

/**
 * Mock ResizeObserver for components that observe element size changes.
 */
export function mockResizeObserver(): void {
  (globalThis as any).ResizeObserver = function () {
    return {
      observe: () => {},
      unobserve: () => {},
      disconnect: () => {},
    };
  };
}

/**
 * Mock Performance API for Mapbox GL and other libraries that use performance measurements.
 */
export function mockPerformanceApi(): void {
  Object.defineProperty(globalThis, 'performance', {
    writable: true,
    value: {
      mark: () => {},
      measure: () => {},
      now: () => Date.now(),
      clearMarks: () => {},
      clearMeasures: () => {},
      getEntriesByType: () => [],
      getEntriesByName: () => [],
    },
  });
}

/**
 * Mock Canvas and WebGL contexts for Mapbox GL and other graphics libraries.
 */
export function mockCanvasAndWebGL(): void {
  (HTMLCanvasElement.prototype.getContext as any) = (contextType: string) => {
    if (contextType === '2d') {
      return {
        fillRect: () => {},
        clearRect: () => {},
        getImageData: () => ({ data: [] }),
        putImageData: () => {},
        createImageData: () => {},
        setTransform: () => {},
        drawImage: () => {},
        save: () => {},
        fillText: () => {},
        restore: () => {},
        beginPath: () => {},
        moveTo: () => {},
        lineTo: () => {},
        closePath: () => {},
        stroke: () => {},
        translate: () => {},
        scale: () => {},
        rotate: () => {},
        arc: () => {},
        fill: () => {},
        measureText: () => ({ width: 0 }),
        transform: () => {},
        rect: () => {},
        clip: () => {},
      } as any;
    } else if (contextType === 'webgl' || contextType === 'experimental-webgl') {
      return {
        canvas: {},
        drawingBufferWidth: 1,
        drawingBufferHeight: 1,
        getShaderParameter: () => true,
        getShaderInfoLog: () => '',
        getProgramParameter: () => true,
        getProgramInfoLog: () => '',
        createShader: () => ({}),
        shaderSource: () => {},
        compileShader: () => {},
        createProgram: () => ({}),
        attachShader: () => {},
        linkProgram: () => {},
        useProgram: () => {},
        createBuffer: () => ({}),
        bindBuffer: () => {},
        bufferData: () => {},
        enableVertexAttribArray: () => {},
        vertexAttribPointer: () => {},
        drawArrays: () => {},
        clear: () => {},
        clearColor: () => {},
        enable: () => {},
        disable: () => {},
        getParameter: (param: number) => {
          if (param === 0x1f00) return 'Mock WebGL Vendor';
          if (param === 0x1f01) return 'Mock WebGL Renderer';
          if (param === 0x1f02) return 'WebGL 1.0';
          return null;
        },
        getExtension: () => null,
        getSupportedExtensions: () => [],
        viewport: () => {},
        scissor: () => {},
        deleteShader: () => {},
        deleteProgram: () => {},
        deleteBuffer: () => {},
        deleteTexture: () => {},
        createTexture: () => ({}),
        bindTexture: () => {},
        texImage2D: () => {},
        texParameteri: () => {},
        generateMipmap: () => {},
        activeTexture: () => {},
        uniform1i: () => {},
        uniform1f: () => {},
        uniform2f: () => {},
        uniform3f: () => {},
        uniform4f: () => {},
        uniformMatrix4fv: () => {},
        getUniformLocation: () => ({}),
        getAttribLocation: () => 0,
      } as any;
    }
    return null;
  };
}

/**
 * Mock URL API for blob handling in libraries like Mapbox GL.
 */
export function mockUrlApi(): void {
  (globalThis as any).URL.createObjectURL = () => 'mock-url';
  (globalThis as any).URL.revokeObjectURL = () => {};
}

/**
 * Mock all Mapbox GL related APIs (Performance, Canvas, WebGL, URL).
 *
 * Use this for components that use Mapbox GL maps.
 *
 * @example
 * ```typescript
 * import { mockMapboxApis } from '@frontend/shared';
 *
 * describe('MapComponent', () => {
 *   beforeEach(() => {
 *     mockMapboxApis();
 *   });
 * });
 * ```
 */
export function mockMapboxApis(): void {
  mockPerformanceApi();
  mockCanvasAndWebGL();
  mockUrlApi();
}

/**
 * Mock all common browser APIs that are often needed in tests.
 */
export function mockAllBrowserApis(): void {
  mockIntersectionObserver();
  mockResizeObserver();
  mockMapboxApis();
}
